import React from 'react';
import debounce from 'lodash/debounce';
import { ExperimentContext } from 'src/components/experiment';
import { putSession, getSession } from '../globals/api/requests';
import { pushEvent } from './data-layer';

type Experiment<T> = {
  id: string;
  baseline: T;
  variants: { [key: string]: T };
};

type State = {
  applicationExperiments: Array<Experiment<any>>;
  selectedVariants: SelectedVariants;
  viewedExperiments: ViewedExperiments;
};

export type SelectedVariants = {
  [key: string]: {
    variantIndex: number;
    selectedVariant: string;
  };
};

export type ViewedExperiments = {
  [experimentId: string]: {
    variant: string;
  };
};

export type ExperimentSession = {
  env?: 'dev' | 'production';
  optimize?: string;
  experiments?: SelectedVariants;
};

const updateViewedExperiments = async (
  viewedExperiments: ViewedExperiments
) => {
  await putSession('viewedExperiments', viewedExperiments);
  const sessionData = await getSession();
  window.SESSION_DATA = sessionData;
  pushEvent(
    'updateViewedExperiments',
    {
      experiments: sessionData.experiments.optimize,
    },
    { eventNameAsKey: false }
  );
};

const ExperimentContextContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { session, experiments } = window.SESSION_DATA;

  const [state, setState] = React.useState<State>({
    applicationExperiments: [],
    selectedVariants: {},
    viewedExperiments: session.viewedExperiments || {},
  });

  const debounceUpdateViewedExperiments = React.useCallback(
    debounce(
      (viewedExperiments: ViewedExperiments) =>
        updateViewedExperiments(viewedExperiments),
      500
    ),
    []
  );

  const viewedExperimentsNeedsUpdate = React.useRef(false);

  React.useEffect(() => {
    // No need to update viewed experiments when this component mounts
    // since we get the session viewed experiments from session init callback
    // in `server/react/middlewares/out.js` when page loads and use that as default state
    if (viewedExperimentsNeedsUpdate.current) {
      debounceUpdateViewedExperiments(state.viewedExperiments);
    } else {
      viewedExperimentsNeedsUpdate.current = true;
    }
  }, [debounceUpdateViewedExperiments, state.viewedExperiments]);

  const methods = React.useMemo(() => {
    const devMode = experiments.env === 'dev';
    return {
      registerExperiment: (experiment: Experiment<any>) => {
        if (!devMode) {
          return;
        }

        setState(currState => {
          if (
            currState.applicationExperiments.find(e => e.id === experiment.id)
          ) {
            return currState;
          }
          return {
            ...currState,
            applicationExperiments: [
              ...currState.applicationExperiments,
              experiment,
            ],
          };
        });
      },
      unregisterExperiment: (id: string) => {
        if (!devMode) return;

        setState(prevState => ({
          ...prevState,
          applicationExperiments: prevState.applicationExperiments.filter(
            experiment => experiment.id !== id
          ),
        }));
      },
      selectVariant: (experiment: Experiment<any>, variant: string) => {
        putSession('selectedVariants', {
          [experiment.id]: variant,
        });
        setState(currState => ({
          ...currState,
          selectedVariants: {
            ...currState.selectedVariants,
            [experiment.id]: {
              ...experiment,
              selectedVariant: variant,
              variantIndex: -1,
            },
          },
        }));
      },
      setViewedExperiment: (experimentId: string, variant: string) => {
        if (
          state.viewedExperiments[experimentId] &&
          state.viewedExperiments[experimentId].variant === variant
        ) {
          return;
        }

        setState(currState => ({
          ...currState,
          viewedExperiments: {
            ...currState.viewedExperiments,
            [experimentId]: {
              variant,
            },
          },
        }));
      },
    };
  }, [experiments.env, state.viewedExperiments]);

  const value = React.useMemo(() => {
    return {
      showEditor: experiments.env === 'dev',
      sessionId: session.id,
      selectedVariants: {
        ...experiments.experiments,
        ...state.selectedVariants,
      },
      viewedExperiments: state.viewedExperiments,
      experiments: state.applicationExperiments,
      ...methods,
    };
  }, [
    experiments.experiments,
    experiments.env,
    session.id,
    state.applicationExperiments,
    state.selectedVariants,
    state.viewedExperiments,
    methods,
  ]);
  return (
    <ExperimentContext.Provider value={value}>
      {children}
    </ExperimentContext.Provider>
  );
};

export default ExperimentContextContainer;
